import * as angular from 'angular';
import { States } from "../../shared/config/constant.module";
import template from './plantPerformance.tpl.html';
import AuthDataServiceName from "../../services/auth/authData.service";
import { Plant, NavigationModel } from '../../models/navigation';
import PlantPerformanceServiceName, { PlantPerformanceService } from '../../services/dashboard/plantPerformance.service';
import { apsViewModels } from "../../shared/dto/APS.ViewModels";
import * as _ from "lodash";
import { LocationsService } from '../../services/locations/locations.service';
import { NavigationService } from '../../services/navigation/navigation.service';
import { LoaderService } from '../../components/loader/loader.service';
import * as moment from 'moment';

let requires = [AuthDataServiceName, PlantPerformanceServiceName];

export interface PlantVisibility {
  plantName: string;
  isVisible: boolean;
}

function plantPerformanceConfig($stateProvider: angular.ui.IStateProvider) {
  $stateProvider.state({
    name: States.PLANT_PERFORMANCE,
    url: '/plant-performance-dashboard',
    controller: 'PlantPerformanceCtrl as vm',
    template: template,
    data: { pageTitle: 'Company Performance' }
  });
}

class PlantPerformanceCtrl {

  dataItems: apsViewModels.SnapshotDataItem[];
  plantData: apsViewModels.SnapshotDataItem[];
  plantList: Plant[];
  plantVisiblity: PlantVisibility[];
  dailyPackDate: string;
  showHelpWindow: boolean;
  yearFormat: string;
  monthFormat: string;
  dayFormat: string;
  pyFormat: string;

  private navigation: NavigationModel;
  private $state: angular.ui.IStateService;

  constructor(
    locationsService: LocationsService,
    navigationService: NavigationService,
    loaderService: LoaderService,
    plantPerformanceService: PlantPerformanceService,
    $state: angular.ui.IStateService) {

    this.$state = $state;

    var promise = locationsService.initialize().then(() => {
      return navigationService.getNavigation().then((nav: NavigationModel) => {
        this.navigation = nav;
        this.plantList = nav.plants().filter(x => !x.isDisabled);

        plantPerformanceService.getSnapshotData().then((data: apsViewModels.Snapshot) => {
          this.dataItems = data.snapshotDataItems.map(x => this.calculatePercents(x));
          this.plantData = _.filter(this.dataItems, plants => 
            plants.shopId == null && plants.tankId == null && this.plantList.some(x => x.name === plants.plantName));
          this.plantVisiblity = this.plantData.map(x => ({ plantName: x.plantName, isVisible: true }));
          this.dailyPackDate = data.dataThru;
          this.yearFormat = moment(this.dailyPackDate).year().toString();
          this.monthFormat = moment(this.dailyPackDate).format('MMM').toUpperCase();
          this.dayFormat = moment(this.dailyPackDate).format('M/DD');
          this.pyFormat = (moment(this.dailyPackDate).year() - 1).toString();
        });
      });
    });

    loaderService.loadingPromise(promise);
  }

  calculatePercents(item: apsViewModels.SnapshotDataItem) {

    item.percentOfAopMonth = ((Math.round(item.monthPercent  * 10) / 10)/ (Math.round(item.aopMonth * 10) / 10)) * 100;  // Compare rounded to 1 decimal place
    item.percentOfAopYear = ((Math.round(item.yearPercent  * 10) / 10)/ (Math.round(item.aopYear * 10) / 10)) * 100;  // Compare rounded to 1 decimal place

    item.monthStatus = this.getStatus(item.percentOfAopMonth);
    item.yearStatus = this.getStatus(item.percentOfAopYear);

    return item;
  }

  getStatus(percent: number) {
    if (percent >= 100) {
      return "pass";
    }
    return "fail";
  }

  goToPlant(plantLocationId: string, tankNumber: string, shopNumber: string) {
    let selectedPlant = _.find(this.plantList, plants => plants.id === plantLocationId);
    this.navigation.selectPlant(selectedPlant);
    if (shopNumber != null) {
      this.navigation.selectPlantShop(selectedPlant, _.find(selectedPlant.shops, shops => shops.name === shopNumber));
    }

    let selectedLocation = shopNumber != null ? "Shop " + shopNumber : tankNumber != null ? "Tank " + tankNumber : "";
    this.$state.go(States.PLANT_PERFORMANCE_CHARTS, { selectedLocation: selectedLocation });
  }

  getTanks(plantId: number) {
    return _.filter(this.dataItems, items => items.plantId === plantId && items.tankId != null && items.shopId == null);
  }

  getShops(plantId: number, tankId: number) {
    return _.filter(this.dataItems, items => items.plantId === plantId && items.tankId === tankId && items.shopId != null);
  }

  displayPlant(plantName: string) {
    return _.find(this.plantVisiblity, plants => plants.plantName === plantName).isVisible;
  }

  togglePlantVisiblity(plantName: string) {
    _.find(this.plantVisiblity, plants => plants.plantName === plantName).isVisible =
      !_.find(this.plantVisiblity, plants => plants.plantName === plantName).isVisible;
  }

  showHelp() {
    this.toggleHelpWindow();
  }

  toggleHelpWindow() {
    this.showHelpWindow = !this.showHelpWindow;
  }

  helpText: string = `  
    <div class="header">
      Performance Dashboard
    </div>
    <div class="subheader">
      <span> Data Source:  </span>
      <span class="detail">Legacy AS400</span>
    </div>             
    <div class="subheader">
      <u>Plant/Tank</u>:
    </div>  
    <div class="detail">
      At the plant or tank level, <b>Pack To Melt (PTM)</b> is displayed for the most recent 
      date that data is available (see upper right of screen for exact date).  
      This is displayed in the <b>Daily PTM</b> column (e.g. "1/29 PTM"), the <b>MTD PTM</b> column (e.g. "JAN PTM"), and 
      the <b>YTD PTM</b> column (e.g. "2020 PTM").
      The <b>previous year</b> value is listed for comparison in the <b>PY</b> columns (e.g. "Jan 2019" and "2019 PTM").<br/><br/>
      <span class="formula">PTM = Packed Tons / Melted Tons * 100</span><br/>
      <span class="footnote">[where Melted Tons > 0]</span><br/><br/>
      Melted Tons is taken from the Materials Usage / Commodity File in the legacy system, summarized by plant, tank, and date.<br/><br/>
      <span class="formula">Melted Tons = SUM(LBS * Yield / 100 / 2000)</span><br/>
      <span class="footnote">[where LBS is # of batcheds * pounds of material and Yield 
        is % of material consumed from Commodity File]</span><br/><br/>
      Packed Tons is taken from the Job Records system in legacy, summarized by plant, tank, and date.<br/><br/>
      <span class="formula">Packed Tons = SUM(Weight * Gross * .0045)</span><br/>
      <span class="footnote">[where .0045 is 144/32000 to convert oz-gross to tons]</span>
    </div>         
    <div class="detail">
      <b>Annual Operating Plan (AOP)</b> values are also displayed for MTD and YTD comparisons.  The AOP values are pulled from 
      yearly spreadsheets maintained at corporate.
    </div>  
    <div class="detail">
      The <b>MTD PTM</b> and <b>YTD PTM</b> columns are also color coded at the plant level to indicate the current % of AOP. 
      The values will be highlighted in <span class="red">red</span> for any 
      percent <b>below 100</b> and <span class="green">green</span> for 
      anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = PTM / AOP * 100</span>
    </div>
    <div class="subheader">
        <u>Shop</u>:
    </div>   
    <div class="detail">
      At the shop level, Percent Pack is displayed for the most recent date that data is available (see upper 
        right of screen for exact date)
      This is displayed in the <b>Daily PTM</b> column, the <b>MTD PTM</b> column (PTM percent for the month), and 
      the <b>YTD PTM</b> column (PTM percent for the year).
      The <b>previous year</b> value is listed for comparison in the <b>PY</b> columns.<br/><br/>
      Percent Pack is calculated from data in the Job Records system in legacy.  All records where 
      speed > 0 and production hours > 0 are included.<br/><br/>
      <span class="formula">% Pack = rounded to tenths(Gross / Shearcuts * 100)</span><br/>
      <span class="footnote">[where shearcuts > 0]</span><br/><br/>
      <span class="formula">Shearcuts = Actual Speed * 60 * Actual Hours / 144</span>
    </div>  
    <div class="detail">
      <b>Annual Operating Plan (AOP)</b> values are also displayed for MTD and YTD comparisons.  The AOP values are pulled from 
      yearly spreadsheets maintained at corporate.
    </div>  
    <div class="detail">
      The <b>MTD PTM</b> and <b>YTD PTM</b> columns are also color coded at the plant level to indicate the current % of AOP. 
      The values will be highlighted in <span class="red">red</span> for any 
      percent <b>below 100</b> and <span class="green">green</span> for anything <b>100% and above</b>.<br/><br/>
      <span class="formula">% of AOP = PTM (in this case Percent Pack) / AOP * 100</span>
    </div>
  `;
}

export default angular.module('aps.controller.plantperformancedashboard', requires)
  .config(plantPerformanceConfig)
  .controller('PlantPerformanceCtrl', PlantPerformanceCtrl)
  .name;



// WEBPACK FOOTER //
// ./src/app/controllers/dashboard/plantPerformance.controller.ts